import React, { useState } from 'react';
import { Workflow, AlertCircle, Layers, Plus } from '@/components/icons';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from './dialog';

interface MermaidImportModalProps {
  isOpen: boolean;
  onClose: () => void;
  onImport: (code: string) => void;
  error?: string | null;
  hasExistingNodes?: boolean;
}

const PLACEHOLDER = `graph TD
  web[React Frontend] -->|HTTPS| api[Node.js API]
  api --> db[(PostgreSQL)]
  api -.-> cache[(Redis)]`;

const MermaidImportModal: React.FC<MermaidImportModalProps> = ({
  isOpen,
  onClose,
  onImport,
  error,
  hasExistingNodes = false,
}) => {
  const [code, setCode] = useState('');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (code.trim()) {
      onImport(code);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-lg gap-0 overflow-hidden p-0">
        <div className="flex items-center gap-2 p-6 border-b transition-colors bg-card border-border dark:bg-card dark:border-border">
          <div className="p-2 bg-blue-500/10 rounded-lg">
            <Workflow className="w-5 h-5 text-blue-500 dark:text-blue-400" />
          </div>
          <div>
            <DialogTitle className="text-lg font-bold text-foreground dark:text-foreground">
              Import Mermaid
            </DialogTitle>
            <DialogDescription className="text-xs text-muted-foreground dark:text-muted-foreground">
              Paste a Mermaid flowchart or class diagram — from ChatGPT, Claude, or your docs.
            </DialogDescription>
          </div>
        </div>

        <form
          onSubmit={handleSubmit}
          className="p-6 space-y-4 bg-card dark:bg-card transition-colors"
        >
          {hasExistingNodes ? (
            <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-blue-500/10 border border-blue-500/20">
              <Layers className="w-4 h-4 text-blue-500" />
              <span className="text-xs font-medium text-blue-600 dark:text-blue-400">
                Will add to your existing diagram
              </span>
            </div>
          ) : (
            <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-slate-500/10 border border-border/20">
              <Workflow className="w-4 h-4 text-muted-foreground" />
              <span className="text-xs font-medium text-muted-foreground dark:text-muted-foreground">
                Will create a new diagram
              </span>
            </div>
          )}

          {error && (
            <div className="flex items-start gap-2 px-3 py-2 rounded-lg bg-red-500/10 border border-red-500/20">
              <AlertCircle className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />
              <span className="text-sm text-red-600 dark:text-red-400">{error}</span>
            </div>
          )}

          <div>
            <label
              htmlFor="mermaid-code"
              className="block text-sm font-medium mb-2 text-foreground dark:text-muted-foreground"
            >
              Mermaid code
            </label>
            <textarea
              id="mermaid-code"
              rows={9}
              className="w-full rounded-xl p-4 font-mono text-xs focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none resize-none transition-all bg-muted text-foreground border-border placeholder:text-muted-foreground dark:bg-card dark:text-foreground dark:border-border dark:placeholder:text-muted-foreground"
              placeholder={PLACEHOLDER}
              value={code}
              onChange={(e) => setCode(e.target.value)}
              autoFocus
            />
            <p className="mt-2 text-xs text-muted-foreground dark:text-muted-foreground">
              Flowcharts (<code>graph</code>/<code>flowchart</code>) with nodes, edges, labels and
              subgraphs, and class diagrams (<code>classDiagram</code>) with members and relations.
            </p>
          </div>

          <div className="pt-2">
            <button
              type="submit"
              disabled={!code.trim()}
              className="w-full bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-500 hover:to-cyan-500 text-white font-semibold py-3 px-4 rounded-xl flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-lg shadow-blue-500/20"
            >
              {hasExistingNodes ? (
                <>
                  <Plus className="w-5 h-5" />
                  Add to Diagram
                </>
              ) : (
                <>
                  <Workflow className="w-5 h-5" />
                  Import Diagram
                </>
              )}
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default MermaidImportModal;
